"use client";

import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import BupiLink from "@/components/atoms/link";
import BupiLinkButton from "@/components/atoms/linkButton";
import WrapperBlueStyle from "@/components/atoms/wrapper-blue-style";
import PlayButton from "@/components/atoms/play-icon";
import { ModalConfiguration, useModalContext } from "@ama-pt/agora-design-system";
import { useTranslations } from "next-intl";
import { InfoTextWithVideoBlockProps } from "@/page-builder/fragments/InfoTextWithVideoBlock/types";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";
import { getAccessibleImageProps } from "@/lib/image-alt";

type VideoModalProps = {
  videoSrc?: string | null;
  youtubeUrl?: string | null;
  videoTitle?: string;
};

const VideoModal = ({
  videoSrc,
  youtubeUrl,
  videoTitle,
}: VideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const getYoutubeEmbedUrl = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11
      ? `https://www.youtube.com/embed/${match[2]}?autoplay=1`
      : null;
  };
        const t = useTranslations('InfoTextWithVideoBlockSection');

  const isYoutubeVideo = !!youtubeUrl;
  const embedSrc = isYoutubeVideo && youtubeUrl ? getYoutubeEmbedUrl(youtubeUrl) : null;

  return (
    <div
      className="z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-black w-full max-w-screen-xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video w-full">
          {isYoutubeVideo ? (
            <iframe
              id="youtube-video-player"
              title={videoTitle}
              src={embedSrc || ""}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
              tabIndex={0}
            />
          ) : (
            <video
              ref={videoRef}
              id="video-modal-title"
              src={videoSrc || ""}
              controls
              autoPlay
              className="w-full h-full"
              tabIndex={0}
            >
              {t("errorMessage")}
            </video>
          )}
        </div>
      </div>
    </div>
  );
};

export const InfoTextWithVideoBlockSection = ({
  title,
  description,
  imagePlaceholder,
  imagePosition,
  youtubeUrl,
  video,
  background,
  ctas,
}: InfoTextWithVideoBlockProps) => {
    const t = useTranslations("InfoTextWithVideoBlockSection");
  
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const imageDirection = imagePosition === "left" ? -1 : 1;
  
  const [modalCounter, setModalCounter] = useState(0);

  const { show, hide } = useModalContext();

  const closeModal = () => {
    setModalCounter(prev => prev + 1);
    
    const iframes = document.querySelectorAll('iframe[src*="youtube"]');
    iframes.forEach(iframe => {
      iframe.setAttribute('src', '');
    });
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
    
    hide();
  };

  useEffect(() => {
    const handleModalClose = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('.agora-modal-dialog-header button') || 
        (target.tagName === 'BUTTON' && target.closest('.agora-modal-dialog-header'))
      ) {
        closeModal();
      }
    };
    
    document.addEventListener('click', handleModalClose, true);
    
    return () => {
      document.removeEventListener('click', handleModalClose, true);
    };
  }, [hide]);

  const openModalHandler = () => {
    show(
      <VideoModal 
        key={modalCounter}
        videoSrc={video?.url || null} 
        youtubeUrl={youtubeUrl} 
        videoTitle={title}
      />, 
      {
        title: title,
        closeButtonLabel: 'Fechar',
        onClose: closeModal,
      } as ModalConfiguration
    );
  };

  const hasVideo = youtubeUrl || (video && video.url);

  const Content = () => (
    <section
      ref={sectionRef}
      className="pb-64 md:pb-96 pt-64 container mx-auto"
    >
      <div className="grid grid-cols-1 xl:grid-cols-8 gap-32 md:items-center">
        <div
          className={`relative flex col-span-4 justify-end order-1 ${
            imagePosition === "left" ? "md:order-1" : "md:order-2"
          }`}
        >
          <div className="relative p-0 md:px-32 w-full">
            <motion.img
              src={imagePlaceholder?.url || "/default-video-placeholder.jpg"}
              {...getAccessibleImageProps(imagePlaceholder?.alt || t("clickVideo"))}
              className="w-full h-auto z-0 rounded-lg"
              initial={{ opacity: 0, x: imageDirection * 80 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{
                x: { duration: 2, ease: "easeOut" },
                opacity: { duration: 1.5 },
              }}
            />

            {hasVideo && (
              <button
                onClick={openModalHandler}
                className="absolute inset-0 flex items-center justify-center"
                aria-label="Reproduzir vídeo"
              >
                <PlayButton size={40} color="#ffffff" hoverColor="#ffffff" />
              </button>
            )}
          </div>
        </div>

        <div
          className={`flex col-span-4 flex-col justify-center space-y-8 order-2 ${
            imagePosition === "left" ? "md:order-2" : "md:order-1"
          }`}
        >
          <div id="video-section-title">
            <Heading title={title} />
          </div>
          <div
            dangerouslySetInnerHTML={{ __html: convertLexicalWithInternalLinks( description as any ) }}
            className="text-m-regular text-neutral-900 py-8 prose prose-ul:my-0 marker:text-neutral-900"
          />
          
          <div className="flex flex-col md:flex-row md:items-start mt-12 gap-8 md:gap-0 md:space-x-24">
            {ctas &&
              ctas.map((cta, index) =>
                cta?.style === "solid" ? (
                  <BupiLinkButton
                    key={index}
                    label={cta.label}
                    link={cta.url}
                    target={cta.target || "_self"}
                    hasIcon
                    leadingIcon="agora-line-arrow-right-circle"
                  />
                ) : (
                  <BupiLink
                    key={index}
                    label={cta.label}
                    url={cta.url}
                    target={cta.target || "_self"}
                    hasIcon
                    className="py-16"
                  />
                )
              )}
          </div>
        </div>
      </div>
    </section>
  );

  return background === "green" ? (
    <WrapperBlueStyle>
      <Content />
    </WrapperBlueStyle>
  ) : (
    <Content />
  );
};
